/**
 * Painel master da Berytus (só equipe): visão geral com funil, lista de clientes e ficha de cada conta
 * com plano, prazo, inativação, usuários e "entrar como cliente".
 */
import { useState, type ReactNode } from 'react';
import { Link, Navigate, Outlet, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Dialog from '@radix-ui/react-dialog';
import * as Dropdown from '@radix-ui/react-dropdown-menu';
import { Copy, Eye, MoreHorizontal, Search, X } from 'lucide-react';
import type { AccountState, AdminPlanBody, AdminStats, AdminTenantDetail, AdminTenantRow, AdminUserRow, Plan } from '@berytus/contracts';
import { ROLE_LABEL } from '@berytus/contracts';
import { api, dateTime, int } from '../api.ts';
import { useMe } from '../components/AuthGate.tsx';
import { ErrorBox, Loading, Tag } from '../components/ui.tsx';
import { Head, useSearchBox, useUrlState } from './MeliChannel.tsx';

export const STATE: Record<AccountState, { label: string; tone: string }> = {
  trial: { label: 'Em teste', tone: 'info' },
  trial_expired: { label: 'Teste vencido', tone: 'warn' },
  paid: { label: 'Ativo (pago)', tone: 'ok' },
  internal: { label: 'Ativo (interno)', tone: 'ok' },
  suspended: { label: 'Inativo', tone: 'err' },
};
const ROLE = ROLE_LABEL;
const ACTION: Record<string, string> = {
  'tenant.plan': 'Plano alterado',
  'tenant.extend_trial': 'Teste estendido',
  'tenant.suspend': 'Conta inativada',
  'tenant.reactivate': 'Conta reativada',
  'tenant.impersonate': 'Entrou como cliente',
  'user.password_reset_email': 'Link de nova senha enviado por e-mail',
  'user.password_reset_link': 'Link de nova senha gerado para copiar',
  'user.activate': 'Usuário reativado',
  'user.deactivate': 'Usuário desativado',
  'user.verify_email': 'E-mail confirmado pela equipe',
  'user.revoke_sessions': 'Sessões encerradas',
  'user.role': 'Papel alterado',
};

const day = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '—');
const ago = (iso: string | null) => {
  if (!iso) return 'nunca';
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  return d <= 0 ? 'hoje' : d === 1 ? 'ontem' : `há ${int(d)} dias`;
};
const age = (days: number) => (days === 0 ? 'desde hoje' : days === 1 ? '1 dia' : days < 60 ? `${int(days)} dias` : `${int(Math.floor(days / 30))} meses`);

/** Rotas /admin: só para a equipe Berytus. */
export function AdminGate() {
  const me = useMe();
  if (!me.user.platformRole) return <Navigate to="/" replace />;
  return <Outlet />;
}

export function StateTag({ t }: { t: Pick<AdminTenantRow, 'state' | 'trialDaysLeft'> }) {
  const s = STATE[t.state];
  return <Tag tone={t.state === 'trial' && (t.trialDaysLeft ?? 99) <= 5 ? 'warn' : s.tone}>{s.label}{t.state === 'trial' && t.trialDaysLeft != null ? ` · ${t.trialDaysLeft}d` : ''}</Tag>;
}

// ---------------------------------------------------------------------------------------------
// Visão geral

export function AdminOverview() {
  const q = useQuery({ queryKey: ['admin', 'stats'], queryFn: () => api<AdminStats>('/admin/stats') });
  return (
    <>
      <Head title="Painel Berytus"><p>Clientes cadastrados, situação das contas e ativação do período de teste.</p></Head>
      {q.isPending ? <Loading /> : q.error ? <ErrorBox error={q.error} /> : <StatsBody s={q.data} />}
    </>
  );
}

function StatsBody({ s }: { s: AdminStats }) {
  const f = s.funnel;
  const steps: [string, number][] = [['Cadastraram', f.signedUp], ['Confirmaram o e-mail', f.verified], ['Conectaram um marketplace', f.connected], ['Publicaram um anúncio', f.published], ['Contrataram', f.paid]];
  const sv = s.server;
  const used = sv.diskTotal ? 1 - (sv.diskFree ?? 0) / sv.diskTotal : null;
  const gb = (b: number) => `${(b / 1024 ** 3).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} GB`;
  return (
    <div className="stack-y">
      {used != null && used >= 0.8 && (
        <div className="error" role="alert">
          <b>Disco do servidor {Math.round(used * 100)}% ocupado</b> ({gb(sv.diskFree ?? 0)} livres de {gb(sv.diskTotal!)}).
          {' '}Com menos de 1 GB livre a Berytus recusa fotos novas; com o disco cheio o banco de dados para. Aumente o disco.
        </div>
      )}
      <div className="kpis">
        <Link className="kpi" to="/admin/clientes"><span className="k-label">Clientes</span><span className="k-value">{int(s.tenants)}</span><span className="k-note">{int(s.signups7d)} nos últimos 7 dias · {int(s.signups30d)} em 30</span></Link>
        <Link className="kpi" to="/admin/clientes?state=trial"><span className="k-label">Em teste</span><span className="k-value">{int(s.byState.trial)}</span><span className="k-note">{int(s.expiringIn7d)} vencem em até 7 dias</span></Link>
        <Link className={`kpi${s.byState.trial_expired ? ' alert' : ''}`} to="/admin/clientes?state=trial_expired"><span className="k-label">Teste vencido</span><span className="k-value">{int(s.byState.trial_expired)}</span><span className="k-note">bloqueados aguardando contratação</span></Link>
        <Link className="kpi" to="/admin/clientes?state=paid"><span className="k-label">Pagantes</span><span className="k-value">{int(s.byState.paid)}</span><span className="k-note">{int(s.byState.internal)} internos</span></Link>
        <Link className="kpi" to="/admin/clientes?state=suspended"><span className="k-label">Inativos</span><span className="k-value">{int(s.byState.suspended)}</span><span className="k-note">inativados pela equipe</span></Link>
        {sv.cpu != null && (
          <div className={`kpi${sv.cpu >= 0.85 ? ' alert' : ''}`}><span className="k-label">CPU do servidor</span><span className="k-value">{Math.round(sv.cpu * 100)}%</span>
            <span className="k-note">carga 1/5/15 min: {sv.load?.map((x) => x.toLocaleString('pt-BR')).join(' / ')} · {sv.cores} núcleo(s)</span></div>
        )}
        {sv.memTotal != null && (
          <div className={`kpi${1 - (sv.memFree ?? 0) / sv.memTotal >= 0.9 ? ' alert' : ''}`}><span className="k-label">Memória do servidor</span><span className="k-value">{Math.round((1 - (sv.memFree ?? 0) / sv.memTotal) * 100)}%</span>
            <span className="k-note">{gb(sv.memFree ?? 0)} livres de {gb(sv.memTotal)}</span></div>
        )}
        {used != null && (
          <div className={`kpi${used >= 0.8 ? ' alert' : ''}`}><span className="k-label">Disco do servidor</span><span className="k-value">{Math.round(used * 100)}%</span><span className="k-note">{gb(sv.diskFree ?? 0)} livres · banco {gb(sv.dbBytes)}</span></div>
        )}
      </div>
      <div className="cols">
        <section className="panel">
          <h2>Funil do cadastro pelo site</h2>
          {steps.map(([label, n]) => (
            <div key={label} style={{ display: 'grid', gridTemplateColumns: '190px 1fr 70px', gap: 10, alignItems: 'center', padding: '6px 0' }}>
              <span className="small">{label}</span>
              <div style={{ height: 10, borderRadius: 999, background: 'var(--neutral-bg)', overflow: 'hidden' }}>
                <div style={{ width: `${f.signedUp ? (100 * n) / f.signedUp : 0}%`, height: '100%', background: 'var(--brand)' }} />
              </div>
              <span className="tnum small" style={{ textAlign: 'right' }}>{int(n)}{f.signedUp ? ` · ${Math.round((100 * n) / f.signedUp)}%` : ''}</span>
            </div>
          ))}
        </section>
        <section className="panel">
          <h2>Cadastros recentes</h2>
          {s.recent.length === 0 ? <p className="muted small">Nenhum cliente ainda.</p> : s.recent.map((t) => (
            <div className="row-line" key={t.id}>
              <Link to={`/admin/clientes/${t.id}`}>{t.name}</Link>
              <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}><span className="small muted">{day(t.createdAt)}</span><StateTag t={t} /></span>
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Lista de clientes

const FILTERS: [string, string][] = [
  ['', 'Todos'], ['trial', 'Em teste'], ['expiring', 'Vencem em 7 dias'], ['trial_expired', 'Teste vencido'],
  ['paid', 'Pagantes'], ['internal', 'Internos'], ['suspended', 'Inativos'], ['not_connected', 'Sem canal'],
];

export function AdminTenants() {
  const [params, set] = useUrlState();
  const [search, setSearch] = useSearchBox(params, set);
  const state = params.get('state') ?? '';
  const sort = params.get('sort') ?? 'created';
  const q = useQuery({
    queryKey: ['admin', 'tenants', params.get('q'), state, sort],
    queryFn: () => api<AdminTenantRow[]>(`/admin/tenants?${new URLSearchParams({ ...(params.get('q') ? { q: params.get('q')! } : {}), ...(state ? { state } : {}), sort })}`),
  });
  return (
    <>
      <Head title="Clientes"><p>Todas as empresas da plataforma. Clique numa empresa para ver usuários, plano e histórico.</p></Head>
      <div className="toolbar">
        <label className="search">
          <Search size={16} />
          <span className="sr-only">Buscar</span>
          <input className="input" type="search" placeholder="Nome, CNPJ ou e-mail do dono" value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
        <select className="select" aria-label="Situação" value={state} onChange={(e) => set({ state: e.target.value || null })}>
          {FILTERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select className="select" aria-label="Ordenar" value={sort} onChange={(e) => set({ sort: e.target.value === 'created' ? null : e.target.value })}>
          <option value="created">Mais recentes</option>
          <option value="trial">Teste vence antes</option>
          <option value="login">Último acesso</option>
          <option value="name">Nome</option>
        </select>
      </div>
      <div className="panel flush">
        {q.error ? <div style={{ padding: 18 }}><ErrorBox error={q.error} /></div> : q.isPending ? <Loading /> : !q.data.length ? (
          <div className="empty"><b>Nenhum cliente</b>Nada encontrado com esses filtros.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="simple">
              <thead>
                <tr><th>Empresa</th><th>Situação</th><th>Cliente há</th><th>Dono</th><th>Último acesso</th><th className="num">Usuários</th><th className="num">Produtos</th><th className="num">Canais</th><th className="num">Anúncios</th></tr>
              </thead>
              <tbody>
                {q.data.map((t) => (
                  <tr key={t.id}>
                    <td><Link to={`/admin/clientes/${t.id}`}><b>{t.name}</b></Link><div className="small muted">{t.cnpj ?? 'sem CNPJ'}</div></td>
                    <td><StateTag t={t} />{t.state === 'trial' && <div className="small muted">até {day(t.trialEndsAt)}</div>}</td>
                    <td className="small">{age(t.ageDays)}<div className="muted">{day(t.createdAt)}</div></td>
                    <td className="small">{t.owner ? <>{t.owner.name}<div className="muted">{t.owner.email}{!t.owner.emailVerified && <> · <span style={{ color: 'var(--warn)' }}>não confirmado</span></>}</div></> : '—'}</td>
                    <td className="small">{ago(t.lastLoginAt)}</td>
                    <td className="num">{int(t.users)}</td>
                    <td className="num">{int(t.products)}{t.productLimit != null && <span className="muted">/{int(t.productLimit)}</span>}</td>
                    <td className="num">{int(t.channels)}</td>
                    <td className="num">{int(t.listings)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {q.data && q.data.length > 0 && <div className="grid-foot"><span className="tnum">{int(q.data.length)} empresa(s)</span></div>}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// Ficha do cliente

export function AdminTenant() {
  const { id } = useParams();
  const me = useMe();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin', 'tenant', id], queryFn: () => api<AdminTenantDetail>(`/admin/tenants/${id}`) });
  const [dialog, setDialog] = useState<null | 'plan' | 'suspend'>(null);
  const [link, setLink] = useState<string | null>(null);
  const isSuper = me.user.platformRole === 'superadmin';
  const refresh = () => qc.invalidateQueries({ queryKey: ['admin'] });
  const act = useMutation({
    mutationFn: ({ url, body, method = 'POST' }: { url: string; body?: unknown; method?: 'POST' | 'PUT' }) => api<{ url?: string } | undefined>(url, { method, json: body ?? {} }),
    onSuccess: (r) => { if (r && typeof r === 'object' && r.url) setLink(r.url); refresh(); },
  });
  const impersonate = useMutation({
    mutationFn: () => api(`/admin/tenants/${id}/impersonate`, { method: 'POST', json: {} }),
    onSuccess: () => { qc.clear(); location.href = '/'; },
  });

  if (q.isPending) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const { tenant: t, company, users, channels, audit } = q.data;
  const a = company.address;
  const addr = [a.street && `${a.street}${a.number ? `, ${a.number}` : ''}`, a.district, a.city && `${a.city}/${a.state ?? ''}`, a.zip].filter(Boolean).join(' · ');

  return (
    <>
      <div className="page-head">
        <div>
          <p className="small" style={{ margin: 0 }}><Link to="/admin/clientes">← Clientes</Link></p>
          <h1 style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>{t.name} <StateTag t={t} /></h1>
          <p>{t.legalName ?? '—'} · {t.cnpj ?? 'sem CNPJ'} · {t.vertical === 'pharmacy' ? 'Farmácia' : 'E-commerce'}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn" type="button" disabled={t.state === 'suspended' || impersonate.isPending} onClick={() => impersonate.mutate()}><Eye size={16} /> Entrar como cliente</button>
        </div>
      </div>
      {(act.error || impersonate.error) && <div style={{ marginBottom: 12 }}><ErrorBox error={act.error ?? impersonate.error} /></div>}
      {t.state === 'suspended' && (
        <div className="notice err" style={{ marginBottom: 16 }}>
          Conta inativada em {dateTime(t.suspendedAt)}{company.suspendedReason ? <> · motivo: <b>{company.suspendedReason}</b></> : ''}. Ninguém da empresa consegue entrar e a leitura automática dos canais está parada.
        </div>
      )}

      <div className="cols" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', alignItems: 'start' }}>
        <section className="panel">
          <h2>Conta e plano</h2>
          <Line k="Cliente há">{age(t.ageDays)} (desde {day(t.createdAt)})</Line>
          <Line k="Origem">{t.signupSource === 'site' ? 'Cadastro pelo site' : 'Criada pela equipe'}</Line>
          <Line k="Plano">{t.plan === 'trial' ? 'Período de teste' : t.plan === 'paid' ? 'Pago' : 'Interno'}</Line>
          {t.plan === 'trial' && <Line k="Fim do teste">{day(t.trialEndsAt)} {t.trialDaysLeft != null && <span className="muted">({t.trialDaysLeft === 0 ? 'vencido' : `${t.trialDaysLeft} dias`})</span>}</Line>}
          <Line k="Limite de produtos">{t.productLimit == null ? 'sem limite' : `${int(t.products)} de ${int(t.productLimit)}`}</Line>
          <Line k="Anúncios por marketplace">{t.listingLimit == null ? 'sem limite' : `até ${int(t.listingLimit)}`}</Line>
          <Line k="Último acesso">{ago(t.lastLoginAt)}</Line>
          <Line k="Catálogo mestre">
            {isSuper ? (
              <select className="select" aria-label="Catálogo mestre" value={t.catalogRole ?? ''} disabled={act.isPending}
                onChange={(e) => act.mutate({ url: `/admin/tenants/${t.id}/catalog-role`, method: 'PUT', body: { role: e.target.value || null } })}>
                <option value="">Não alimenta</option>
                <option value="curator">Curadoria (corrige o catálogo)</option>
                <option value="contributor">Colaborador (só preenche o vazio)</option>
              </select>
            ) : t.catalogRole === 'curator' ? 'Curadoria' : t.catalogRole === 'contributor' ? 'Colaborador' : 'Não alimenta'}
          </Line>
          {isSuper && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
              {t.plan === 'trial' && [7, 15, 30].map((d) => (
                <button key={d} className="btn sm" type="button" disabled={act.isPending} onClick={() => act.mutate({ url: `/admin/tenants/${t.id}/extend-trial`, body: { days: d } })}>+{d} dias</button>
              ))}
              <button className="btn sm" type="button" onClick={() => setDialog('plan')}>Alterar plano</button>
              {t.state === 'suspended' ? (
                <button className="btn sm primary" type="button" disabled={act.isPending} onClick={() => act.mutate({ url: `/admin/tenants/${t.id}/reactivate` })}>Reativar conta</button>
              ) : (
                <button className="btn sm" type="button" style={{ color: 'var(--err)' }} onClick={() => setDialog('suspend')}>Inativar conta</button>
              )}
            </div>
          )}
        </section>
        <section className="panel">
          <h2>Empresa</h2>
          <Line k="Razão social">{t.legalName ?? '—'}</Line>
          <Line k="Telefone">{company.phone ?? '—'}</Line>
          <Line k="E-mail de contato">{company.contactEmail ?? '—'}</Line>
          <Line k="Endereço">{addr || '—'}</Line>
          <h2 style={{ marginTop: 16 }}>Canais</h2>
          {channels.length === 0 ? <p className="small muted" style={{ margin: 0 }}>Nenhum marketplace conectado.</p> : channels.map((c) => (
            <Line key={c.id} k={c.channel === 'mercadolivre' ? 'Mercado Livre' : c.channel}>
              {c.name ?? '—'} <Tag tone={c.status === 'active' ? 'ok' : 'warn'}>{c.status}</Tag>
              {c.lastError && <div className="small" style={{ color: 'var(--err)' }}>{c.lastError}</div>}
            </Line>
          ))}
          <Line k="Produtos · anúncios">{int(t.products)} · {int(t.listings)} ({int(t.published)} pela Berytus)</Line>
        </section>
      </div>

      <section className="panel flush" style={{ marginTop: 16 }}>
        <div style={{ padding: '14px 16px 0' }}><h2 style={{ margin: 0 }}>Usuários</h2></div>
        <div style={{ overflowX: 'auto' }}>
          <table className="simple">
            <thead><tr><th>Usuário</th><th>Papel</th><th>Situação</th><th>Último acesso</th><th className="num">Sessões</th><th /></tr></thead>
            <tbody>
              {users.map((u) => <UserRow key={u.id} u={u} tenantId={t.id} isSuper={isSuper} self={u.id === me.user.id} act={(x) => act.mutate(x)} />)}
            </tbody>
          </table>
        </div>
      </section>

      <section className="panel" style={{ marginTop: 16 }}>
        <h2>Histórico da equipe Berytus</h2>
        {audit.length === 0 ? <p className="small muted" style={{ margin: 0 }}>Nenhuma ação registrada.</p> : audit.map((e) => (
          <div className="row-line" key={e.id}>
            <span>{ACTION[e.action] ?? e.action}{e.targetUser && <span className="muted"> · {e.targetUser}</span>}{describe(e.action, e.details)}</span>
            <span className="small muted">{e.actor ?? 'sistema'} · {dateTime(e.at)}</span>
          </div>
        ))}
      </section>

      {dialog === 'plan' && <PlanDialog t={t} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); refresh(); }} />}
      {dialog === 'suspend' && <SuspendDialog t={t} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); refresh(); }} />}
      {link && <LinkDialog url={link} onClose={() => setLink(null)} />}
    </>
  );
}

function describe(action: string, d: Record<string, unknown>): ReactNode {
  if (action === 'tenant.extend_trial') return <span className="muted"> · +{String(d.days)} dias, até {day(String(d.trialEndsAt))}</span>;
  if (action === 'tenant.suspend') return <span className="muted"> · {String(d.reason)}</span>;
  if (action === 'user.role') return <span className="muted"> · {ROLE[d.from as keyof typeof ROLE]} → {ROLE[d.to as keyof typeof ROLE]}</span>;
  if (action === 'tenant.plan') {
    const to = d.to as { plan: Plan } | undefined;
    return to ? <span className="muted"> · {to.plan === 'trial' ? 'teste' : to.plan === 'paid' ? 'pago' : 'interno'}</span> : null;
  }
  return null;
}

function Line({ k, children }: { k: string; children: ReactNode }) {
  return <div className="row-line"><span className="muted">{k}</span><span style={{ textAlign: 'right' }}>{children}</span></div>;
}

function UserRow({ u, tenantId, isSuper, self, act }: {
  u: AdminUserRow; tenantId: string; isSuper: boolean; self: boolean;
  act: (x: { url: string; body?: unknown; method?: 'POST' | 'PUT' }) => void;
}) {
  const locked = !!u.platformRole && !isSuper;
  return (
    <tr style={u.active ? undefined : { opacity: 0.6 }}>
      <td><b>{u.name}</b><div className="small muted">{u.email}</div></td>
      <td>
        {isSuper ? (
          <select className="select" style={{ height: 30 }} aria-label="Papel" value={u.role} onChange={(e) => act({ url: `/admin/users/${u.id}/role`, method: 'PUT', body: { tenantId, role: e.target.value } })}>
            {Object.entries(ROLE).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        ) : ROLE[u.role]}
        {u.platformRole && <div><Tag tone="info">Equipe Berytus</Tag></div>}
      </td>
      <td className="tags">
        {u.active ? <Tag tone="ok">Ativo</Tag> : <Tag tone="off">Desativado</Tag>}
        {u.emailVerified ? <Tag tone="ok">E-mail confirmado</Tag> : <Tag tone="warn">E-mail não confirmado</Tag>}
      </td>
      <td className="small">{ago(u.lastLoginAt)}<div className="muted">criado em {day(u.createdAt)}</div></td>
      <td className="num">{int(u.sessions)}</td>
      <td style={{ textAlign: 'right' }}>
        {!locked && (
          <Dropdown.Root>
            <Dropdown.Trigger asChild><button className="icon-btn" type="button" aria-label={`Ações para ${u.email}`}><MoreHorizontal size={18} /></button></Dropdown.Trigger>
            <Dropdown.Portal>
              <Dropdown.Content className="menu" align="end" sideOffset={6}>
                <div className="menu-label">Senha</div>
                <Dropdown.Item className="menu-item" onSelect={() => act({ url: `/admin/users/${u.id}/password-reset`, body: { send: true } })}>Enviar link de nova senha por e-mail</Dropdown.Item>
                <Dropdown.Item className="menu-item" onSelect={() => act({ url: `/admin/users/${u.id}/password-reset`, body: { send: false } })}>Gerar link para copiar</Dropdown.Item>
                <div className="menu-sep" />
                {!u.emailVerified && <Dropdown.Item className="menu-item" onSelect={() => act({ url: `/admin/users/${u.id}/verify-email` })}>Marcar e-mail como confirmado</Dropdown.Item>}
                {u.sessions > 0 && <Dropdown.Item className="menu-item" onSelect={() => act({ url: `/admin/users/${u.id}/sessions/revoke` })}>Encerrar sessões abertas</Dropdown.Item>}
                {!self && (u.active ? (
                  <Dropdown.Item className="menu-item" style={{ color: 'var(--err)' }} onSelect={() => { if (confirm(`Desativar ${u.email}? A pessoa é desconectada e não consegue mais entrar.`)) act({ url: `/admin/users/${u.id}/active`, body: { active: false } }); }}>Desativar usuário</Dropdown.Item>
                ) : (
                  <Dropdown.Item className="menu-item" onSelect={() => act({ url: `/admin/users/${u.id}/active`, body: { active: true } })}>Reativar usuário</Dropdown.Item>
                ))}
              </Dropdown.Content>
            </Dropdown.Portal>
          </Dropdown.Root>
        )}
      </td>
    </tr>
  );
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="drawer-overlay" />
        <Dialog.Content className="panel" aria-describedby={undefined}
          style={{ position: 'fixed', top: '10vh', left: '50%', transform: 'translateX(-50%)', width: 'min(520px, calc(100vw - 32px))', zIndex: 60, display: 'grid', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Dialog.Title asChild><h2 style={{ margin: 0 }}>{title}</h2></Dialog.Title>
            <span style={{ flex: 1 }} />
            <Dialog.Close className="icon-btn" aria-label="Fechar"><X size={18} /></Dialog.Close>
          </div>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function PlanDialog({ t, onClose, onSaved }: { t: AdminTenantRow; onClose: () => void; onSaved: () => void }) {
  const [plan, setPlan] = useState<Plan>(t.plan);
  const [ends, setEnds] = useState((t.trialEndsAt ?? new Date(Date.now() + 30 * 86_400_000).toISOString()).slice(0, 10));
  const [products, setProducts] = useState(t.productLimit?.toString() ?? '');
  const [listings, setListings] = useState(t.listingLimit?.toString() ?? '');
  const save = useMutation({
    mutationFn: () => {
      const body: AdminPlanBody = {
        plan,
        trialEndsAt: plan === 'trial' ? new Date(`${ends}T23:59:59-03:00`).toISOString() : null,
        productLimit: products ? Number(products) : null,
        listingLimit: listings ? Number(listings) : null,
      };
      return api(`/admin/tenants/${t.id}/plan`, { method: 'PUT', json: body });
    },
    onSuccess: onSaved,
  });
  const pick = (p: Plan) => {
    setPlan(p);
    if (p === 'trial') { setProducts(products || '10'); setListings(listings || '10'); } else { setProducts(''); setListings(''); }
  };
  return (
    <Modal title="Alterar plano" onClose={onClose}>
      <div className="seg" role="radiogroup" style={{ justifySelf: 'start' }}>
        {(['trial', 'paid', 'internal'] as const).map((p) => <button key={p} type="button" aria-pressed={plan === p} onClick={() => pick(p)}>{p === 'trial' ? 'Teste' : p === 'paid' ? 'Pago' : 'Interno'}</button>)}
      </div>
      {plan === 'trial' && <div className="field"><label htmlFor="pl-end">Fim do teste</label><input className="input" id="pl-end" type="date" value={ends} onChange={(e) => setEnds(e.target.value)} /></div>}
      <div className="cols" style={{ gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="field"><label htmlFor="pl-p">Limite de produtos</label><input className="input" id="pl-p" inputMode="numeric" placeholder="sem limite" value={products} onChange={(e) => setProducts(e.target.value.replace(/\D/g, ''))} /></div>
        <div className="field"><label htmlFor="pl-l">Anúncios por marketplace</label><input className="input" id="pl-l" inputMode="numeric" placeholder="sem limite" value={listings} onChange={(e) => setListings(e.target.value.replace(/\D/g, ''))} /></div>
      </div>
      <p className="small muted" style={{ margin: 0 }}>Em branco = sem limite. Mudar para Pago ou Interno desbloqueia a conta na hora.</p>
      {save.error && <ErrorBox error={save.error} />}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn primary" type="button" disabled={save.isPending} onClick={() => save.mutate()}>Salvar</button>
        <button className="btn" type="button" onClick={onClose}>Cancelar</button>
      </div>
    </Modal>
  );
}

function SuspendDialog({ t, onClose, onSaved }: { t: AdminTenantRow; onClose: () => void; onSaved: () => void }) {
  const [reason, setReason] = useState('');
  const save = useMutation({ mutationFn: () => api(`/admin/tenants/${t.id}/suspend`, { method: 'POST', json: { reason } }), onSuccess: onSaved });
  return (
    <Modal title={`Inativar ${t.name}`} onClose={onClose}>
      <p style={{ margin: 0 }}>Todos os usuários da empresa são desconectados e não conseguem entrar; a leitura automática dos canais para. Nada é apagado e a conta pode ser reativada a qualquer momento.</p>
      <div className="field"><label htmlFor="sus-r">Motivo (fica no histórico)</label><input className="input" id="sus-r" autoFocus value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex.: inadimplência, pedido do cliente" /></div>
      {save.error && <ErrorBox error={save.error} />}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn primary" type="button" style={{ background: 'var(--err)', borderColor: 'var(--err)' }} disabled={reason.trim().length < 3 || save.isPending} onClick={() => save.mutate()}>Inativar conta</button>
        <button className="btn" type="button" onClick={onClose}>Cancelar</button>
      </div>
    </Modal>
  );
}

function LinkDialog({ url, onClose }: { url: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <Modal title="Link para criar nova senha" onClose={onClose}>
      <p style={{ margin: 0 }} className="small">Vale por 1 hora e só pode ser usado uma vez. Envie direto para a pessoa (WhatsApp, por exemplo).</p>
      <input className="input" readOnly value={url} onFocus={(e) => e.target.select()} />
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn primary" type="button" onClick={async () => { await navigator.clipboard.writeText(url).catch(() => {}); setCopied(true); }}><Copy size={16} /> {copied ? 'Copiado' : 'Copiar link'}</button>
        <button className="btn" type="button" onClick={onClose}>Fechar</button>
      </div>
    </Modal>
  );
}

/** Faixa fixa no topo (permanente, com a cor do Painel Berytus) enquanto a equipe Berytus vê a conta de um cliente. */
export function ImpersonationBar() {
  const me = useMe();
  const qc = useQueryClient();
  const exit = useMutation({
    mutationFn: () => api<{ restored: boolean }>('/admin/impersonate/exit', { method: 'POST' }),
    onSuccess: (r) => { qc.clear(); location.href = r.restored ? `/admin/clientes/${me.tenant.id}` : '/login'; },
  });
  if (!me.impersonating) return null;
  return (
    <div className="impersonation-bar" role="status" style={{ display: 'flex', gap: 12, alignItems: 'center', margin: '0 0 16px', padding: '10px 14px', borderRadius: 8, flexWrap: 'wrap' }}>
      <Eye size={18} />
      <span><b>Modo de visualização</b> · você ({me.impersonating.byName}) está vendo a conta de <b>{me.tenant.name}</b> como {me.user.name}. Nada pode ser alterado.</span>
      <span style={{ flex: 1 }} />
      <button className="btn sm primary" type="button" disabled={exit.isPending} onClick={() => exit.mutate()}>Voltar ao painel Berytus</button>
    </div>
  );
}
